import { Link, useLocation } from 'react-router-dom';
import { ErrorNotice, Skeleton } from '../ui';
import { Page, PageHeader } from './Page';

/**
 * The states a page is in before it has its content (CO-184, OR3, OR19).
 *
 * A page that is loading or could not be read keeps its header: the person
 * still knows where they are, and the failure says what to do. Before this a
 * failed read replaced the whole page with a bare notice and no way to try
 * again. `title` is the page's own title; without it the state fills a
 * section (a tab of Settings, which already has the Settings header).
 *
 * - `PageLoading`: the header, then rows shaped like the page's content.
 * - `PageError`: the header, then what failed and Retry. An unreadable page is
 *   never an empty one: it says that nothing below is known.
 * - `NotFound`: an address that is not a page, with the way back.
 */

export function PageLoading({ title, description, rows = 6, cards = 0 }: { title?: string; description?: string; rows?: number; cards?: number }) {
  const body = (
    <div className="skeleton-page" role="status" aria-label={title ? `Loading ${title.toLowerCase()}` : 'Loading'}>
      {cards > 0 ? (
        <div className="skeleton-page__cards">
          {Array.from({ length: cards }).map((_, i) => (
            <Skeleton key={i} width="100%" height="88px" radius="var(--radius-md)" />
          ))}
        </div>
      ) : null}
      <div className="skeleton-page__rows">
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} width="100%" height="56px" radius="var(--radius-md)" />
        ))}
      </div>
    </div>
  );
  if (!title) return body;
  return (
    <Page>
      <PageHeader title={title} description={description} />
      {body}
    </Page>
  );
}

export function PageError({
  title,
  description,
  error,
  onRetry,
  nextStep = 'Retry reads it again. Until it loads, nothing below is known.',
}: {
  title?: string;
  description?: string;
  error: Error | null;
  onRetry: () => void;
  nextStep?: string;
}) {
  const notice = <ErrorNotice error={error} onRetry={onRetry} nextStep={nextStep} />;
  if (!title) return notice;
  return (
    <Page>
      <PageHeader title={title} description={description} />
      {notice}
    </Page>
  );
}

/** An address that is not a page: what was asked for, and the way back. */
export function NotFound({ home, homeLabel }: { home: string; homeLabel: string }) {
  const { pathname } = useLocation();
  return (
    <Page width="narrow" testId="not-found">
      <PageHeader title="Page not found" description="This address does not match a page in Commerce Ops." />
      <p className="not-found__address">
        <code translate="no">{pathname}</code>
      </p>
      <p>
        <Link className="button button--primary" to={home}>
          {homeLabel}
        </Link>
      </p>
    </Page>
  );
}
